import { useMemo, useState } from "react";
import { ShieldCheck, Warning, XCircle, CheckCircle, ArrowsClockwise, Info } from "@phosphor-icons/react";

const STYLE = {
  PASS: { chip: "text-[var(--c-green)] border-[var(--c-green)]/40 bg-[var(--c-green)]/10", Icon: CheckCircle },
  WARN: { chip: "text-[var(--c-amber)] border-[var(--c-amber)]/40 bg-[var(--c-amber)]/10", Icon: Warning },
  FAIL: { chip: "text-[var(--c-red)] border-[var(--c-red)]/50 bg-[var(--c-red)]/10", Icon: XCircle },
  "SPEC ISSUE": { chip: "text-[var(--c-amber)] border-[var(--c-amber)]/50 bg-[var(--c-amber)]/10", Icon: Info },
  "N/A": { chip: "text-[var(--c-muted)] border-[var(--c-border)] bg-[var(--c-surface-2)]", Icon: Info },
};
const OVERALL = {
  BLOCKED: "text-[var(--c-red)] border-[var(--c-red)]/50 bg-[var(--c-red)]/10",
  "SPEC ISSUE": "text-[var(--c-amber)] border-[var(--c-amber)]/50 bg-[var(--c-amber)]/10",
  WARN: "text-[var(--c-amber)] border-[var(--c-amber)]/40 bg-[var(--c-amber)]/10",
  PASS: "text-[var(--c-green)] border-[var(--c-green)]/40 bg-[var(--c-green)]/10",
};
const ORDER = ["FAIL", "SPEC ISSUE", "WARN", "PASS", "N/A"];

/** Phase 3 compliance matrix: {check_id, clause, expected, actual, status} + blocking checks. */
export default function CompliancePanel({ data, busy, onRun, onSelectPart, selected }) {
  const [filter, setFilter] = useState(null);
  const [open, setOpen] = useState(null);

  const rows = useMemo(() => {
    const list = [...(data?.rows || [])];
    list.sort((a, b) => ORDER.indexOf(a.status) - ORDER.indexOf(b.status)
      || a.check_id.localeCompare(b.check_id));
    return filter ? list.filter((r) => r.status === filter) : list;
  }, [data, filter]);

  if (!data) {
    return (
      <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-4" data-testid="compliance-empty">
        <ShieldCheck size={20} className="mb-2 text-[var(--c-accent)]" />
        <p className="text-sm text-[var(--c-text)]">Not validated yet</p>
        <p className="mt-1 text-xs text-[var(--c-muted)]">
          The §8.1 generic checks (counts, dimensions, nothing floating, no interference,
          ground level, clearances, mass) and the transformer rule-pack checks run against the
          geometry that is actually drawn.
        </p>
        <button data-testid="compliance-run-btn" disabled={busy} onClick={onRun}
          className="mt-3 flex h-7 items-center gap-1.5 rounded-sm border border-[var(--c-accent)]/60 bg-[var(--c-accent)]/15 px-2.5 text-[11px] text-[var(--c-accent)] hover:bg-[var(--c-accent)]/25 disabled:opacity-40">
          <ShieldCheck size={12} /> Run the checks
        </button>
      </div>
    );
  }

  const counts = data.counts || {};
  return (
    <div className="flex flex-col gap-3" data-testid="compliance-panel">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] px-3 py-2">
        <span className={`rounded-sm border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${OVERALL[data.status] || OVERALL.WARN}`}
          data-testid="compliance-status">{data.status}</span>
        <span className="font-mono text-[10px] text-[var(--c-muted)]">{data.schema}</span>
        <button data-testid="compliance-rerun-btn" disabled={busy} onClick={onRun}
          className="ml-auto flex h-7 items-center gap-1.5 rounded-sm border border-[var(--c-border)] px-2.5 text-[11px] text-[var(--c-text)] hover:border-[var(--c-accent)] disabled:opacity-40">
          <ArrowsClockwise size={12} /> Re-run
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="compliance-filters">
        {ORDER.filter((s) => counts[s]).map((s) => (
          <button key={s} data-testid={`compliance-filter-${s.replace(/\s/g, "-").toLowerCase()}`}
            onClick={() => setFilter((f) => (f === s ? null : s))}
            className={`rounded-sm border px-2 py-0.5 font-mono text-[10px] ${STYLE[s].chip} ${
              filter === s ? "ring-1 ring-[var(--c-accent)]" : ""}`}>
            {s} {counts[s]}
          </button>
        ))}
        {data.stats?.overall_mm && (
          <span className="ml-auto font-mono text-[10px] text-[var(--c-muted)]" data-testid="compliance-envelope">
            envelope {data.stats.overall_mm[0].toLocaleString()} × {data.stats.overall_mm[2].toLocaleString()} ×{" "}
            {data.stats.overall_mm[1].toLocaleString()} mm · {data.stats.instances} instances
          </span>
        )}
      </div>

      {!!data.blockers?.length && (
        <div className="rounded-lg border border-[var(--c-red)]/40 bg-[var(--c-red)]/10 p-3" data-testid="compliance-blockers">
          <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-[var(--c-red)]">
            Blocked — {data.blockers.length} check(s) must pass before this arrangement is issued
          </div>
          <div className="font-mono text-[11px] text-[var(--c-text)]">{data.blockers.join(" · ")}</div>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)]">
        <div className="border-b border-[var(--c-border)] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-[var(--c-muted)]">
          Compliance matrix · {rows.length} check(s)
        </div>
        <table className="w-full text-[12px]">
          <tbody>
            {rows.map((r) => {
              const { chip, Icon } = STYLE[r.status] || STYLE["N/A"];
              const isOpen = open === r.check_id;
              const hit = r.parts?.length ? r.parts[0] : null;
              return (
                <tr key={r.check_id} data-testid={`compliance-row-${r.check_id}`}
                  onClick={() => { setOpen(isOpen ? null : r.check_id); if (hit) onSelectPart?.(hit); }}
                  className={`cursor-pointer border-b border-[var(--c-border)]/50 align-top hover:bg-[var(--c-surface-2)] ${
                    hit && selected === hit ? "bg-[var(--c-accent)]/10" : ""}`}>
                  <td className="w-6 px-2 py-1.5">
                    <Icon size={13} className={chip.split(" ")[0]} />
                  </td>
                  <td className="px-1 py-1.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-mono text-[10px] text-[var(--c-accent)]">{r.check_id}</span>
                      <span className="font-mono text-[9px] text-[var(--c-muted)]">{r.clause}</span>
                      {r.blocking && r.status === "FAIL" && (
                        <span className="rounded-sm border border-[var(--c-red)]/50 px-1 font-mono text-[9px] text-[var(--c-red)]">
                          blocking
                        </span>
                      )}
                    </div>
                    <div className="text-[var(--c-text)]">{r.title}</div>
                    <div className="font-mono text-[10px] text-[var(--c-muted)]">
                      expected {r.expected} · actual{" "}
                      <span className={r.status === "PASS" ? "text-[var(--c-green)]" : chip.split(" ")[0]}>
                        {r.actual}
                      </span>
                    </div>
                    {isOpen && r.note && (
                      <div className="mt-1 rounded-sm border border-[var(--c-border)] bg-[var(--c-bg)] px-2 py-1 text-[11px] text-[var(--c-muted)]"
                        data-testid={`compliance-note-${r.check_id}`}>
                        {r.note}
                        {!!r.parts?.length && (
                          <div className="mt-1 font-mono text-[9px] text-[var(--c-accent)]">
                            parts: {r.parts.join(", ")}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="w-20 px-2 py-1.5 text-right">
                    <span className={`rounded-sm border px-1.5 py-0.5 font-mono text-[9px] ${chip}`}>
                      {r.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
